import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, type TemplateRef } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEyeOff } from '@ng-icons/lucide';
import { HlmSeparator } from '@lite-workspace/shared/helm-ui/separator';
import { GalleryMasonryComponent } from './gallery-masonry.component';
import type { PublicGalleryPhoto } from './public-photo.models';

export interface SceneStackScene {
  readonly id: string;
  readonly title: string;
  readonly photos: PublicGalleryPhoto[];
  /** Backend-sanitized HTML (<p>/<br>/<a>); absent on the cabinet preview. */
  readonly description?: string | null;
  /** A scene the client hid — the heading dims and carries the «Скрыто» badge. */
  readonly hidden?: boolean;
}

// The ONE scene stack of a published gallery — the client page and the cabinet
// preview paint this same tree, so the rhythm (lab gallery-public-view.tsx: soft
// divider, uniform 64 px blocks, +48 px only between consecutive scenes) can
// never drift between them. The host decides what sits beside the heading and
// what a scene's body is: the client hands its own menu and its masonry with
// hearts/downloads, the preview takes the plain masonry below.
@Component({
  selector: 'gallery-scene-stack',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GalleryMasonryComponent, HlmSeparator, NgIcon, NgTemplateOutlet],
  providers: [provideIcons({ lucideEyeOff })],
  host: { class: 'flex flex-col gap-16' },
  template: `
    <hlm-separator />
    <div class="flex flex-col gap-16 [&>section+section]:mt-12">
      @for (scene of scenes(); track scene.id) {
        <section class="flex scroll-mt-20 flex-col gap-16" [attr.data-scene-id]="scene.id">
          @if (hasHeader(scene)) {
            <header class="relative flex flex-col items-center gap-8 text-center">
              <div class="flex items-center justify-center gap-2">
                @if (!single()) {
                  <h2
                    class="text-[length:var(--text-4xl)] leading-10 font-normal"
                    [class.text-ink]="!scene.hidden"
                    [class.text-c3]="scene.hidden"
                  >
                    {{ scene.title }}
                  </h2>
                }
                @if (scene.hidden) {
                  <span
                    class="bg-bg2 text-c3 inline-flex shrink-0 items-center gap-1 rounded-[var(--r-button)] px-2 py-1
                  text-xs leading-4"
                  >
                    <ng-icon name="lucideEyeOff" size="16" />
                    Скрыто
                  </span>
                }
                @if (headerExtra(); as extra) {
                  <ng-container [ngTemplateOutlet]="extra" [ngTemplateOutletContext]="{ $implicit: scene }" />
                }
              </div>
              @if (scene.description) {
                <div class="scene-note text-c3 w-full max-w-[800px] text-base leading-6" [innerHTML]="scene.description"></div>
              }
            </header>
          }
          @if (body(); as bodyTemplate) {
            <ng-container [ngTemplateOutlet]="bodyTemplate" [ngTemplateOutletContext]="{ $implicit: scene }" />
          } @else {
            <gallery-masonry
              [photos]="scene.photos"
              [columnCount]="columnCount()"
              [gapPx]="gapPx()"
              [densityScale]="densityScale()"
            />
          }
        </section>
      }
    </div>
  `,
  styles: `
    /* The scene note is backend-sanitized HTML (<p>/<br>/<a> only); Angular's
       [innerHTML] sanitizer re-checks it. Style the whitelisted tags here. */
    .scene-note p + p {
      margin-top: 0.5rem;
    }
    .scene-note a {
      color: var(--acc-2);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  `,
})
export class GallerySceneStackComponent {
  readonly scenes = input.required<readonly SceneStackScene[]>();
  readonly columnCount = input.required<number>();
  readonly gapPx = input.required<number>();
  readonly densityScale = input(1);
  /** Rendered beside the heading with the scene as `$implicit` — the client's scene menu. */
  readonly headerExtra = input<TemplateRef<unknown> | null>(null);
  /** Replaces the plain masonry with the host's own scene body (scene as `$implicit`). */
  readonly body = input<TemplateRef<unknown> | null>(null);
  /** The gallery's RAW scene count, when the host hands in a filtered `scenes` list. */
  readonly sceneCount = input<number | null>(null);

  // T4.1: a gallery with ONE scene never names it — the gallery title already
  // does. The criterion is the RAW scene count (`sceneCount`, else `scenes`),
  // the same one the client page's sticky bar reads.
  protected readonly single = computed(() => (this.sceneCount() ?? this.scenes().length) === 1);

  // With the heading gone a lone scene can be left with an empty header box —
  // and the section's own 64 px gap would still hold it open above the photos.
  // So the header stands only while something can stand in it.
  protected hasHeader(scene: SceneStackScene): boolean {
    return !this.single() || !!this.headerExtra() || !!scene.description || !!scene.hidden;
  }
}
